import type { Comparator } from 'domain/sort-by'
import type { FC } from 'react'

import { Button, Icon } from '@s-group/design-system-components'
import { IconControlPlus } from '@s-group/design-system-icons'
import { GeneralError } from 'components/errors/ErrorPage'
import { Modal } from 'components/Modal/Modal'
import { SkeletonLoader } from 'components/SkeletonLoader'
import { remoteData } from 'domain/remote-data'
import { Comparators } from 'domain/sort-by'
import { pipe } from 'fp-ts/function'
import { showGlobalLoadingIndicatorVar } from 'lib/apollo/reactive-vars'
import { useTranslation } from 'react-i18next'
import { handleRequestErrors } from 'services/error-handler'
import { ModalService, useModal } from 'services/Modal'
import styled from 'styled-components'
import { useStoreId } from 'utils/hooks/store/use-store-id'

import { AddToShoppinglist } from './icons/AddToShoppinglist'
import { useGetListsQuery } from './queries/use-get-user-lists'
import { useAddToList } from './support/use-add-to-list'
import { useCreateNewList } from './support/use-create-new-list'
import { ShoppinglistTracking } from './tracking'

type PartialListType = {
  id: string
  name: string
}

type ListItem = {
  id: string
  name: string
  itemCount: number
  selected: boolean
}
const sortSelectedListItemsToBottom: Comparator<{ selected: boolean }> = (a, b) =>
  Comparators.gte(a.selected, b.selected)

const _AddToListModal: FC<{
  className?: string
  product: { ean: string }
  onCreateNew: () => void
}> = ({ className, onCreateNew, product }) => {
  const { t } = useTranslation()
  const { storeId } = useStoreId()
  const listsQuery = pipe(
    useGetListsQuery(storeId),
    remoteData.map((lists) =>
      lists.map((list) => ({
        id: list.id,
        name: list.name,
        itemCount: list.items.length,
        // popover has a tick-icon,
        // if ean of the current product is
        // found within the list items
        selected: list.items.some((i) => i.product && i.product.ean === product.ean),
      })),
    ),
    remoteData.map((lists) => lists.sort(sortSelectedListItemsToBottom)),
  )

  const modal = useModal<PartialListType>()

  return (
    <Modal.Modal
      onClose={modal.onCancel}
      className={className}
      title={t('Add to shopping list')}
      dataTestId="add-to-shoppinglist-modal"
    >
      {pipe(
        listsQuery,
        remoteData.fold(
          () => null,
          () => <Loading />,
          () => <Error onClose={modal.onCancel} />,
          (items) => <Success items={items} onCreateNew={onCreateNew} onSubmit={modal.onSubmit} />,
        ),
      )}
    </Modal.Modal>
  )
}

const Loading: FC = () => {
  return (
    <ul>
      <li>
        <SkeletonLoader height={64} />
      </li>
      <li>
        <SkeletonLoader height={64} />
      </li>
      <li>
        <SkeletonLoader height={64} />
      </li>
    </ul>
  )
}

const Error: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation()
  return (
    <div>
      <GeneralError />
      <ButtonWrapper>
        <Button onClick={onClose} primary rounding="small">
          {t('Ok')}
        </Button>
      </ButtonWrapper>
    </div>
  )
}
const ButtonWrapper = styled.div(({ theme }) => ({
  marginTop: theme.spacings.xLarge,
  textAlign: 'center',
}))

const Success: FC<{
  items: ListItem[]
  onCreateNew: () => void
  onSubmit: (x: ListItem) => void
}> = ({ items, onCreateNew, onSubmit }) => {
  const { t } = useTranslation()

  const handleSelect = (list: ListItem) => () => onSubmit(list)

  return (
    <ul>
      <li>
        <CreateNewShoppingListBtn
          id="btn-create-new-shopping-list"
          plain
          iconPos="before"
          rounding="small"
          onClick={onCreateNew}
          icon={<IconControlPlus />}
          data-test-id="btn-create-new-shopping-list"
        >
          {t('New list create')}
        </CreateNewShoppingListBtn>
      </li>
      {items.map((x) => (
        <li key={x.id} data-item-id={x.id} data-selected={x.selected}>
          <ItemTitle>
            <h3>{x.name}</h3>
            <h4>{t('Products in shopping list', { count: x.itemCount })}</h4>
            {x.selected ? <h5>{t('Shoppinglists Product already on list')}</h5> : null}
          </ItemTitle>
          <Button
            alternative
            rounding="small"
            data-test-id={`btn-item-add-${x.id}`}
            disabled={x.selected}
            onClick={handleSelect(x)}
          >
            {t('Shoppinglists Select list to add')}
          </Button>
        </li>
      ))}
    </ul>
  )
}

const ItemTitle = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '1',
  overflow: 'hidden',
  ['h3, h4']: {
    ...theme.sizes.xs,
    fontWeight: theme.fontWeights.regular,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },

  h4: {
    color: theme.colors.grey600,
  },
  h5: {
    fontWeight: theme.fontWeights.regular,
    color: theme.colors.primary,
  },
}))

// special case for add button
// #desiredbydesign
const CreateNewShoppingListBtn = styled(Button)(({ theme }) => ({
  paddingLeft: 0,
  paddingRight: 0,
  color: 'inherit',

  svg: {
    path: {
      fill: theme.colors.grey800,
    },
  },

  '&:hover, &:active': {
    outline: 'none',
    boxShadow: 'none',
  },

  '&:hover': {
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    color: theme.colors.primary400,

    svg: {
      path: {
        fill: theme.colors.primary400,
      },
    },
  },
}))

const AddToListModal = styled(_AddToListModal)(({ theme }) => ({
  width: '600px',
  ul: {
    listStyleType: 'none',

    li: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacings.medium,
    },

    ['li + li']: {
      borderTop: '1px solid',
      borderColor: theme.colors.grey200,
    },
  },
}))

const _AddToListContainer: FC<{
  className?: string
  product: { ean: string; name: string; quantity: number; quantityUnit: string }
}> = ({ className, product }) => {
  const { t } = useTranslation()
  const { storeId } = useStoreId()

  const addToList = useAddToList(storeId)
  const createNew = useCreateNewList(storeId)

  const onCreateNew = () => {
    showGlobalLoadingIndicatorVar(true)
    createNew()
      .then((newList) => {
        ShoppinglistTracking.CreateShoppingList(newList.name)
      })
      .catch((e) => handleRequestErrors([e]))
      .finally(() => showGlobalLoadingIndicatorVar(false))
  }

  const openAddToListModal = () => {
    ModalService.open<PartialListType>(
      <AddToListModal onCreateNew={onCreateNew} product={product} />,
      {
        onSubmit: (list) => {
          addToList(list, { ...product, isReplaceable: true })
          ShoppinglistTracking.AddItemToList('product_page', list.name, product)
        },
      },
    )
  }

  return (
    <div className={className}>
      <Button
        rounding="small"
        icon={<Icon svg={AddToShoppinglist} />}
        iconPos={'before'}
        onClick={openAddToListModal}
        data-test-id="btn-add-to-shopping-list"
        variant="tonal"
        color="grey"
      >
        {t('Add to shopping list')}
      </Button>
    </div>
  )
}

export const AddToListContainer = styled(_AddToListContainer)(({ theme }) => ({
  marginTop: theme.spacings.small,
}))
